import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import SEO from '../components/SEO';
import { BookOpen, Award, TrendingUp, ArrowRight, CheckCircle, Clock, BarChart3, Share2, Download, Trophy, Star, Bell, Play, Flame, Shield, Compass, Diamond, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import OnboardingModal from '../components/OnboardingModal';
import AdaptivePathWidget from '../components/AdaptivePathWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, token, getAuthHeaders } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [progress, setProgress] = useState({ progress: [], quiz_results: [] });
  const [programs, setPrograms] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [badgesData, setBadgesData] = useState({ badges: [], total_earned: 0 });
  const [reminders, setReminders] = useState([]);
  const [streakData, setStreakData] = useState(null);
  const [levelData, setLevelData] = useState(null);
  const [generatingCert, setGeneratingCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [progRes, progressRes, certsRes, badgesRes, onbRes, remRes, streakRes, levelRes] = await Promise.all([
          axios.get(`${API}/programs`),
          axios.get(`${API}/progress`, { headers, withCredentials: true }),
          axios.get(`${API}/certificates`, { headers, withCredentials: true }),
          axios.get(`${API}/badges`, { headers, withCredentials: true }),
          axios.get(`${API}/onboarding`, { headers, withCredentials: true }).catch(() => ({ data: { completed: false } })),
          axios.get(`${API}/reminders`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/streaks`, { headers, withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/user/level`, { headers, withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setPrograms(progRes.data);
        setProgress(progressRes.data);
        setCertificates(certsRes.data);
        setBadgesData(badgesRes.data);
        setReminders(remRes.data);
        setStreakData(streakRes.data);
        setLevelData(levelRes.data);
        if (!onbRes.data.completed) setShowOnboarding(true);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, [getAuthHeaders]);

  const handleOnboardingComplete = (recommendedProgram) => {
    setShowOnboarding(false);
    if (recommendedProgram) navigate(`/programs/${recommendedProgram}`);
  };

  const completedModules = progress.progress.filter(p => p.completed).length;
  const quizCount = progress.quiz_results.length;
  const avgScore = quizCount > 0 
    ? Math.round(progress.quiz_results.reduce((sum, q) => sum + q.score, 0) / quizCount) 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="dashboard-page">
      <SEO
        title={language === 'fr' ? 'Tableau de Bord' : 'Dashboard'}
        description="Votre tableau de bord IA Academy : progression, streaks, badges et certificats."
        path="/dashboard"
        noIndex
      />
      {showOnboarding && <OnboardingModal onComplete={handleOnboardingComplete} />}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-10 h-10 rounded-lg" data-testid="dashboard-logo" />
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Poppins' }} data-testid="dashboard-title">
              {t('dash_welcome')}, {user?.name?.split(' ')[0]} !
            </h1>
          </div>
          <p className="text-slate-500 mt-1">{t('dash_progress')}</p>
        </div>

        {/* Streak Widget */}
        {streakData && (
          <div className="mb-8 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-5 text-white relative overflow-hidden" data-testid="streak-widget">
            <div className="absolute right-0 top-0 w-32 h-32 opacity-10">
              <Flame className="w-full h-full" />
            </div>
            <div className="flex items-center gap-6 relative z-10">
              {/* Streak Counter */}
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                  <Flame className={`w-8 h-8 ${streakData.current_streak > 0 ? 'text-yellow-200 animate-pulse' : 'text-white/60'}`} />
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Poppins' }} data-testid="streak-count">
                    {streakData.current_streak}
                  </p>
                  <p className="text-sm text-white/80">
                    {streakData.current_streak === 1 ? t('streak_day') : t('streak_days')}
                  </p>
                </div>
              </div>

              {/* Weekly Calendar */}
              <div className="flex gap-1.5 ml-auto" data-testid="streak-weekly">
                {streakData.weekly?.map((day, i) => (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <span className="text-[10px] text-white/60 uppercase">{day.day.slice(0, 2)}</span>
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-medium transition-all ${
                        day.active
                          ? 'bg-white text-orange-600'
                          : 'bg-white/15 text-white/50'
                      }`}
                      data-testid={`streak-day-${i}`}
                    >
                      {day.active ? <Flame className="w-4 h-4" /> : day.date.slice(-2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="hidden sm:flex gap-6 ml-4">
                <div className="text-center">
                  <p className="text-xl font-bold" data-testid="streak-max">{streakData.max_streak}</p>
                  <p className="text-xs text-white/70">{t('streak_max')}</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold" data-testid="streak-total">{streakData.total_active_days}</p>
                  <p className="text-xs text-white/70">{t('streak_total')}</p>
                </div>
              </div>
            </div>
            
            {/* Status bar */}
            <div className="mt-3 flex items-center justify-between text-xs relative z-10">
              <span className={`px-2 py-0.5 rounded-full ${streakData.active_today ? 'bg-white/20' : 'bg-white/10'}`}>
                {streakData.active_today ? t('streak_active_today') : t('streak_not_active')}
              </span>
              {streakData.freeze_available && !streakData.active_today && streakData.current_streak > 0 && (
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/streaks/freeze`, {}, { headers: getAuthHeaders(), withCredentials: true });
                      const res = await axios.get(`${API}/streaks`, { headers: getAuthHeaders(), withCredentials: true });
                      setStreakData(res.data);
                    } catch (err) { console.error(err); }
                  }}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
                  data-testid="streak-freeze-btn"
                >
                  <Shield className="w-3 h-3" /> {t('streak_freeze')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Adaptive Learning Path */}
        <div className="mb-6">
          <AdaptivePathWidget />
        </div>

        {/* Reminders */}
        {reminders.length > 0 && (
          <div className="mb-6 space-y-3" data-testid="reminders-section">
            {reminders.map((r, i) => (
              <div key={i} className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between" data-testid={`reminder-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Bell className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {r.type === 'start'
                        ? (language === 'fr' ? r.message_fr : r.message_en)
                        : (language === 'fr'
                          ? `Continuez votre module : ${r.title_fr}`
                          : `Continue your module: ${r.title_en}`)
                      }
                    </p>
                    <p className="text-xs text-slate-500">
                      {r.type === 'start'
                        ? (language === 'fr' ? 'Commencez maintenant pour gagner votre premier badge !' : 'Start now to earn your first badge!')
                        : (language === 'fr' ? 'Reprenez là où vous en étiez' : 'Pick up where you left off')
                      }
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-orange-500 text-white hover:bg-orange-600 shrink-0"
                  onClick={() => {
                    if (r.type === 'start') navigate('/programs');
                    else navigate(`/programs/${r.program_id}/module/${r.module_id}`);
                  }}
                  data-testid={`reminder-btn-${i}`}
                >
                  <Play className="w-3.5 h-3.5 mr-1" />
                  {language === 'fr' ? 'Continuer' : 'Continue'}
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Level Score Widget */}
        {levelData && (
          <div className="mb-8 bg-white rounded-2xl border border-slate-200 p-5 overflow-hidden" data-testid="level-widget">
            <div className="flex items-center gap-5">
              {/* Level badge */}
              <div className="relative flex-shrink-0">
                <div
                  className="w-20 h-20 rounded-2xl flex flex-col items-center justify-center shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${levelData.tier?.color}22, ${levelData.tier?.color}44)`, border: `2px solid ${levelData.tier?.color}` }}
                >
                  {levelData.level === 1 && <Compass className="w-7 h-7" style={{ color: levelData.tier?.color }} />}
                  {levelData.level === 2 && <BookOpen className="w-7 h-7" style={{ color: levelData.tier?.color }} />}
                  {levelData.level === 3 && <Award className="w-7 h-7" style={{ color: levelData.tier?.color }} />}
                  {levelData.level === 4 && <Diamond className="w-7 h-7" style={{ color: levelData.tier?.color }} />}
                  {levelData.level === 5 && <Crown className="w-7 h-7" style={{ color: levelData.tier?.color }} />}
                  <span className="text-[10px] font-bold mt-0.5" style={{ color: levelData.tier?.color }}>LVL {levelData.level}</span>
                </div>
              </div>

              {/* Level info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Poppins' }} data-testid="level-name">
                    {language === 'fr' ? levelData.tier?.name_fr : levelData.tier?.name_en}
                  </h3>
                  <Badge className="text-xs" style={{ backgroundColor: `${levelData.tier?.color}20`, color: levelData.tier?.color, borderColor: `${levelData.tier?.color}40` }}>
                    {levelData.score}%
                  </Badge>
                </div>
                <Progress value={levelData.score} className="h-2.5 mb-2" />
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{language === 'fr' ? 'Score global' : 'Overall score'}: <strong className="text-slate-700">{levelData.score}/100</strong></span>
                  {levelData.next_tier && (
                    <span data-testid="level-next">
                      {language === 'fr'
                        ? `${levelData.points_to_next} pts pour ${levelData.next_tier.name_fr}`
                        : `${levelData.points_to_next} pts to ${levelData.next_tier.name_en}`}
                    </span>
                  )}
                </div>
              </div>

              {/* Breakdown mini */}
              <div className="hidden lg:flex gap-3 flex-shrink-0" data-testid="level-breakdown">
                {levelData.breakdown && [
                  { key: 'modules', label: 'Modules', val: `${levelData.breakdown.modules.done}/${levelData.breakdown.modules.total}` },
                  { key: 'quizzes', label: 'Quiz', val: `${levelData.breakdown.quizzes.done}/${levelData.breakdown.quizzes.total}` },
                  { key: 'avg_score', label: 'Score', val: `${levelData.breakdown.avg_score.value}%` },
                  { key: 'streak', label: 'Streak', val: `${levelData.breakdown.streak.days}j` },
                ].map(b => (
                  <div key={b.key} className="text-center px-2">
                    <p className="text-sm font-bold text-slate-700">{b.val}</p>
                    <p className="text-[10px] text-slate-400">{b.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-sm transition-shadow" data-testid="stat-modules">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-500">{t('dash_modules_done')}</span>
              <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
                <BookOpen className="w-4.5 h-4.5 text-blue-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>{completedModules}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-sm transition-shadow" data-testid="stat-quizzes">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-500">{t('dash_quizzes_done')}</span>
              <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Award className="w-4.5 h-4.5 text-emerald-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>{quizCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-sm transition-shadow" data-testid="stat-score">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-500">{t('dash_avg_score')}</span>
              <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-4.5 h-4.5 text-amber-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>{avgScore}%</p>
          </div>
        </div>

        {/* Badges Preview */}
        {badgesData.badges.length > 0 && (
          <div className="mb-8" data-testid="dashboard-badges">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Poppins' }}>
                Badges
                <span className="ml-2 text-sm font-normal text-slate-400">
                  {badgesData.total_earned}/{badgesData.badges.length}
                </span>
              </h2>
              <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => navigate('/badges')} data-testid="view-all-badges">
                {language === 'fr' ? 'Voir tout' : 'View all'} <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {badgesData.badges.filter(b => b.earned).slice(0, 8).map((badge) => (
                <div
                  key={badge.badge_id}
                  className="shrink-0 bg-white rounded-xl border border-slate-200 p-3 w-24 text-center hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => navigate('/badges')}
                  data-testid={`dash-badge-${badge.badge_id}`}
                >
                  <div
                    className="w-10 h-10 mx-auto rounded-xl flex items-center justify-center mb-1.5"
                    style={{ backgroundColor: `${badge.color}15` }}
                  >
                    <Trophy className="w-5 h-5" style={{ color: badge.color }} />
                  </div>
                  <p className="text-[11px] font-medium text-slate-700 leading-tight truncate">
                    {language === 'fr' ? badge.name_fr : badge.name_en}
                  </p>
                </div>
              ))}
              {badgesData.total_earned === 0 && (
                <div className="bg-white rounded-xl border border-dashed border-slate-300 p-4 flex-1 text-center">
                  <Star className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">
                    {language === 'fr' ? 'Commencez à collecter des badges !' : 'Start collecting badges!'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Programs */}
        <div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins' }}>
            {t('dash_continue')}
          </h2>
          {programs.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <p className="text-slate-500">{t('dash_no_progress')}</p>
              <Button className="mt-4 bg-blue-600 text-white" onClick={() => navigate('/programs')}>
                {t('hero_explore')}
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-6">
              {programs.map((prog) => {
                const progModules = progress.progress.filter(p => p.module_id?.startsWith(`mod_${prog.program_id.split('_')[1]}`));
                const completed = progModules.filter(p => p.completed).length;
                const total = prog.modules_count;
                const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

                return (
                  <div
                    key={prog.program_id}
                    className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() => navigate(`/programs/${prog.program_id}`)}
                    data-testid={`dash-program-${prog.program_id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Poppins' }}>
                          {language === 'fr' ? prog.title_fr : prog.title_en}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" /> {prog.duration}
                          </Badge>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-600 transition-colors" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">{completed}/{total} {t('modules')}</span>
                        <span className="text-blue-600 font-medium">{pct}%</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Quiz Results */}
        {progress.quiz_results.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins' }}>
              {language === 'fr' ? 'Quiz récents' : 'Recent Quizzes'}
            </h2>
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {progress.quiz_results.slice(-5).reverse().map((result, i) => (
                <div key={i} className="flex items-center justify-between p-4" data-testid={`quiz-result-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${result.score >= 70 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                      {result.score >= 70 ? (
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <TrendingUp className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">{result.quiz_id}</p>
                      <p className="text-xs text-slate-400">{result.correct}/{result.total} {language === 'fr' ? 'correctes' : 'correct'}</p>
                    </div>
                  </div>
                  <Badge className={result.score >= 70 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}>
                    {result.score}%
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certificates Section */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Poppins' }}>
              {t('cert_title')}
            </h2>
            {certificates.length > 0 && (
              <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => navigate('/certificates')} data-testid="view-all-certs">
                {language === 'fr' ? 'Voir tout' : 'View all'} <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </div>

          {/* Generate certificate buttons for programs */}
          <div className="grid sm:grid-cols-2 gap-4">
            {programs.map((prog) => {
              const existingCert = certificates.find(c => c.program_id === prog.program_id);
              const handleGenerate = async () => {
                setGeneratingCert(prog.program_id);
                try {
                  const res = await axios.post(`${API}/certificates/generate`, 
                    { program_id: prog.program_id },
                    { headers: getAuthHeaders(), withCredentials: true }
                  );
                  setCertificates(prev => {
                    const exists = prev.find(c => c.cert_id === res.data.cert_id);
                    return exists ? prev : [res.data, ...prev];
                  });
                } catch (err) {
                  console.error('Generate cert error:', err);
                }
                setGeneratingCert(null);
              };

              return (
                <div
                  key={prog.program_id}
                  className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between"
                  data-testid={`cert-program-${prog.program_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${existingCert ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                      <Award className={`w-5 h-5 ${existingCert ? 'text-emerald-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {language === 'fr' ? prog.title_fr : prog.title_en}
                      </p>
                      <p className="text-xs text-slate-400">{prog.duration}</p>
                    </div>
                  </div>
                  {existingCert ? (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs gap-1 text-blue-600 border-blue-200"
                        onClick={() => navigate(`/certificate/${existingCert.cert_id}`)}
                        data-testid={`view-cert-btn-${prog.program_id}`}
                      >
                        {t('cert_view')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs gap-1 text-orange-600 border-orange-200"
                        onClick={() => {
                          const url = `${window.location.origin}/certificate/${existingCert.cert_id}`;
                          navigator.clipboard.writeText(url);
                        }}
                        data-testid={`share-cert-btn-${prog.program_id}`}
                      >
                        <Share2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      className="text-xs bg-blue-600 text-white hover:bg-blue-700 active:scale-95 transition-transform"
                      onClick={handleGenerate}
                      disabled={generatingCert === prog.program_id}
                      data-testid={`generate-cert-btn-${prog.program_id}`}
                    >
                      {generatingCert === prog.program_id ? (
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>{t('cert_generate')}</>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
